import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Download,
    Image as ImageIcon,
    Film,
    File as FileIcon,
    Calendar,
    Send,
    AlertCircle,
    User,
    Star,
    Check,
    MessageCircle,
    Clock,
    Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { downloadStatic } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const NAME_KEY = "vsf_share_viewer_name";

function fmtBytes(n) {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
function fmtTs(s) {
    if (s == null) return "";
    const t = Math.max(0, Math.floor(s));
    return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
}
function isImage(f) {
    return (f.content_type || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)$/i.test(f.name || "");
}
function isVideo(f) {
    return (f.content_type || "").startsWith("video/") || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(f.name || "");
}

function FileTypeIcon({ file }) {
    if (isImage(file)) return <ImageIcon className="h-4 w-4 text-[#E5A93D]" />;
    if (isVideo(file)) return <Film className="h-4 w-4 text-[#E5A93D]" />;
    return <FileIcon className="h-4 w-4 text-zinc-500" />;
}

export default function PublicShare() {
    const { slug } = useParams();
    const [share, setShare] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Viewer identity (cached in localStorage so they don't have to retype)
    const [viewerName, setViewerName] = useState(() => {
        try { return localStorage.getItem(NAME_KEY) || ""; } catch { return ""; }
    });
    const [needsName, setNeedsName] = useState(false);
    const [tempName, setTempName] = useState("");
    const pendingActionRef = useRef(null); // callback to run after name is entered

    const load = async () => {
        try {
            const { data } = await api.get(`/share/${slug}`);
            setShare(data);
        } catch (e) {
            setError(e.response?.data?.detail || "Deze share bestaat niet");
        }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

    const saveName = () => {
        const n = tempName.trim().slice(0, 80);
        if (!n) { toast.error("Vul je naam in"); return; }
        setViewerName(n);
        try { localStorage.setItem(NAME_KEY, n); } catch {}
        setNeedsName(false);
        const cb = pendingActionRef.current;
        pendingActionRef.current = null;
        if (typeof cb === "function") cb(n);
    };

    const askNameThen = (cb) => {
        if (viewerName) { cb(viewerName); return; }
        pendingActionRef.current = cb;
        setTempName("");
        setNeedsName(true);
    };

    const downloadAll = async () => {
        if (!share?.files?.length) return;
        try { await api.post(`/share/${slug}/track-download`); } catch {}
        // One single ZIP download — browsers on Windows block multi-file
        // download loops (only the first of N clicks survives the popup blocker).
        const a = document.createElement("a");
        a.href = `${process.env.REACT_APP_BACKEND_URL}/api/share/${slug}/zip`;
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(`Download gestart — ${share.files.length} bestand(en) in één ZIP. Even geduld bij grote bestanden.`);
    };

    const downloadOne = async (f) => {
        try { await api.post(`/share/${slug}/track-download`); } catch {}
        downloadStatic(f.url, f.name);
    };

    const submitComment = (file, text, timestamp_seconds) => {
        return new Promise((resolve) => {
            askNameThen(async (name) => {
                try {
                    const { data } = await api.post(`/share/${slug}/comments`, {
                        file_url: file.url,
                        text,
                        author_name: name,
                        timestamp_seconds: timestamp_seconds == null ? null : timestamp_seconds,
                    });
                    setShare((s) => s ? { ...s, comments: [...(s.comments || []), data] } : s);
                    toast.success("Opmerking toegevoegd");
                    resolve(true);
                } catch (e) {
                    toast.error(e.response?.data?.detail || "Kon opmerking niet plaatsen");
                    resolve(false);
                }
            });
        });
    };

    const toggleReaction = (file, key, current) => {
        askNameThen(async (name) => {
            try {
                const newVal = !current;
                const { data } = await api.post(`/share/${slug}/reactions`, {
                    file_url: file.url,
                    author_name: name,
                    [key]: newVal,
                });
                setShare((s) => {
                    if (!s) return s;
                    const others = (s.reactions || []).filter(r => !(r.author_name === name && r.file_url === file.url));
                    return { ...s, reactions: [...others, { ...(data || {}), [key]: newVal }] };
                });
                if (key === "is_favorite") toast.success(newVal ? "Toegevoegd aan favorieten" : "Uit favorieten");
                if (key === "is_approved") toast.success(newVal ? "Goedgekeurd" : "Goedkeuring ingetrokken");
            } catch (e) {
                toast.error(e.response?.data?.detail || "Mislukt");
            }
        });
    };

    if (loading) {
        return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">Laden…</div>;
    }
    if (error || !share) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
                <div className="text-center max-w-md">
                    <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                    <h1 className="font-display font-bold text-2xl mb-2">Niet gevonden</h1>
                    <p className="text-sm text-zinc-500">{error || "Deze share bestaat niet of is verlopen."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="public-share-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
                {/* Header */}
                <div className="mb-10 pb-8 border-b border-zinc-100">
                    <div className="flex items-center gap-2 mb-4">
                        <Send className="h-4 w-4 text-[#E5A93D]" />
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D]">Vastgoed Social Factory</span>
                    </div>
                    <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tight" data-testid="share-title">
                        {share.title || "Bestanden gedeeld met jou"}
                    </h1>
                    {share.message && (
                        <p className="text-zinc-500 mt-4 whitespace-pre-wrap leading-relaxed">{share.message}</p>
                    )}
                    <div className="text-xs text-zinc-500 mt-6 flex items-center gap-4 flex-wrap">
                        {share.owner_name && <span>Gedeeld door <span className="text-zinc-600">{share.owner_name}</span></span>}
                        {share.expires_at && (
                            <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Verloopt op {new Date(share.expires_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "long", year: "numeric" })}
                            </span>
                        )}
                    </div>
                </div>

                {/* Identity bar */}
                <div className="flex items-center gap-3 p-3 bg-[#FFFFFF] border border-zinc-100 rounded-md mb-8">
                    <User className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                    {viewerName ? (
                        <div className="flex items-center justify-between flex-1 gap-3">
                            <div className="text-sm">
                                <span className="text-zinc-500">Reageer als </span>
                                <span className="text-zinc-950 font-medium">{viewerName}</span>
                            </div>
                            <button
                                onClick={() => { setNeedsName(true); setTempName(viewerName); }}
                                className="text-xs text-zinc-500 hover:text-zinc-950"
                                data-testid="share-change-name-btn"
                            >
                                Wijzig
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => { setNeedsName(true); setTempName(""); }}
                            className="text-sm text-zinc-600 hover:text-zinc-950 text-left flex-1"
                            data-testid="share-set-name-btn"
                        >
                            Klik hier om je naam in te vullen
                        </button>
                    )}
                </div>

                {(!share.files || share.files.length === 0) ? (
                    <div className="text-center py-12 text-zinc-500 text-sm">Er zijn nog geen bestanden in deze share.</div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                            <div className="text-sm text-zinc-500">{share.files.length} bestand(en)</div>
                            <button
                                type="button"
                                onClick={downloadAll}
                                className="inline-flex items-center gap-2 h-10 px-5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98] transition"
                                data-testid="download-all-btn"
                            >
                                <Download className="h-4 w-4" />
                                Alles downloaden
                            </button>
                        </div>

                        <div className="space-y-8" data-testid="public-file-list">
                            {share.files.map((f, i) => (
                                <FileCard
                                    key={i}
                                    file={f}
                                    onDownload={() => downloadOne(f)}
                                    testid={`public-file-${i}`}
                                    comments={(share.comments || []).filter(c => c.file_url === f.url)}
                                    reactions={(share.reactions || []).filter(r => r.file_url === f.url)}
                                    viewerName={viewerName}
                                    submitComment={submitComment}
                                    toggleReaction={toggleReaction}
                                />
                            ))}
                        </div>
                    </>
                )}

                <footer className="mt-12 pt-6 border-t border-zinc-100 flex items-center justify-center">
                    <a
                        href="https://vastgoed.social-factory.be"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 text-zinc-500 hover:text-zinc-600 transition group"
                    >
                        <img src="/social-factory-logo.png" alt="Social Factory" className="h-5 w-auto opacity-50 group-hover:opacity-90 transition invert" />
                        <span className="text-[10px] uppercase tracking-[0.25em]">
                            Powered by <span className="text-zinc-500 group-hover:text-[#E5A93D] transition">Vastgoed Social Factory</span>
                        </span>
                    </a>
                </footer>
            </div>

            {needsName && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setNeedsName(false)}>
                    <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display font-bold text-xl mb-1">Je naam</h3>
                        <p className="text-sm text-zinc-500 mb-4">Zo weten de makers wie de feedback achterlaat.</p>
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Naam</Label>
                        <Input
                            autoFocus
                            value={tempName}
                            onChange={(e) => setTempName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveName()}
                            placeholder="Bv. Jan Janssens"
                            className="h-11 bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                            data-testid="share-name-input"
                        />
                        <div className="flex gap-2 mt-4">
                            <Button onClick={() => setNeedsName(false)} variant="ghost" className="flex-1 text-zinc-600">Annuleer</Button>
                            <Button onClick={saveName} className="flex-1 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold" data-testid="share-name-save-btn">
                                Opslaan
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function FileCard({ file, onDownload, testid, comments, reactions, viewerName, submitComment, toggleReaction }) {
    const showImage = isImage(file);
    const showVideo = isVideo(file);
    const videoRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [newText, setNewText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [attachTs, setAttachTs] = useState(true);

    // Reaction aggregates for photos
    const favs = useMemo(() => (reactions || []).filter(r => r.is_favorite), [reactions]);
    const apps = useMemo(() => (reactions || []).filter(r => r.is_approved), [reactions]);
    const myReaction = (reactions || []).find(r => r.author_name === viewerName) || {};
    const iFav = !!myReaction.is_favorite;
    const iApp = !!myReaction.is_approved;

    const seekTo = (s) => {
        if (!videoRef.current) return;
        try { videoRef.current.currentTime = Math.max(0, s); videoRef.current.play().catch(() => {}); } catch {}
    };

    const onSubmit = async () => {
        const text = newText.trim();
        if (!text) return;
        setSubmitting(true);
        const ts = showVideo && attachTs ? currentTime : null;
        const ok = await submitComment(file, text, ts);
        if (ok) setNewText("");
        setSubmitting(false);
    };

    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md overflow-hidden" data-testid={testid}>
            {showVideo && (
                <div className="aspect-video bg-zinc-50">
                    <video
                        ref={videoRef}
                        src={file.url}
                        controls
                        preload="metadata"
                        className="w-full h-full object-contain"
                        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                        data-testid={`${testid}-video`}
                    />
                </div>
            )}
            {showImage && (
                <a href={file.url} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-zinc-50 overflow-hidden">
                    <img src={file.url} alt={file.name} className="w-full h-full object-contain" loading="lazy" />
                </a>
            )}

            <div className="p-4 flex items-center gap-3 flex-wrap">
                <FileTypeIcon file={file} />
                <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-950 truncate">{file.name}</div>
                    <div className="text-[11px] text-zinc-500">{fmtBytes(file.size)} · {file.content_type || "bestand"}</div>
                </div>
                <button
                    type="button"
                    onClick={onDownload}
                    className="inline-flex items-center gap-1.5 text-xs px-3 h-9 border border-zinc-200 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/[0.05] text-zinc-800 hover:text-zinc-950 rounded-sm transition active:scale-95"
                    data-testid="public-file-download"
                >
                    <Download className="h-3.5 w-3.5" />
                    Download
                </button>
            </div>

            {/* Favorite + Approve toggles — photos only */}
            {showImage && (
                <div className="px-4 pb-4 flex items-center gap-2 flex-wrap" data-testid={`${testid}-reactions`}>
                    <button
                        type="button"
                        onClick={() => toggleReaction(file, "is_favorite", iFav)}
                        className={`inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-sm border transition active:scale-95 ${iFav ? "border-yellow-400/60 bg-yellow-400/10 text-yellow-700" : "border-zinc-200 hover:border-yellow-400/40 text-zinc-600 hover:text-yellow-700"}`}
                        data-testid={`${testid}-favorite-btn`}
                    >
                        <Star className={`h-3.5 w-3.5 ${iFav ? "fill-yellow-300" : ""}`} />
                        {iFav ? "Favoriet" : "Maak favoriet"}
                        {favs.length > 0 && <span className="text-[10px] font-mono opacity-70">· {favs.length}</span>}
                    </button>
                    <button
                        type="button"
                        onClick={() => toggleReaction(file, "is_approved", iApp)}
                        className={`inline-flex items-center gap-1.5 text-xs px-3 h-9 rounded-sm border transition active:scale-95 ${iApp ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-700" : "border-zinc-200 hover:border-emerald-400/40 text-zinc-600 hover:text-emerald-700"}`}
                        data-testid={`${testid}-approve-btn`}
                    >
                        <Check className="h-3.5 w-3.5" />
                        {iApp ? "Goedgekeurd" : "Keur goed"}
                        {apps.length > 0 && <span className="text-[10px] font-mono opacity-70">· {apps.length}</span>}
                    </button>
                </div>
            )}

            {/* Comment form */}
            <div className="px-4 pb-4 pt-2 border-t border-zinc-100">
                <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <MessageCircle className="h-3 w-3" /> Opmerkingen
                    {showVideo && (
                        <span className="ml-auto inline-flex items-center gap-2">
                            <label className="flex items-center gap-1.5 cursor-pointer text-zinc-500 normal-case tracking-normal text-xs">
                                <input
                                    type="checkbox"
                                    checked={attachTs}
                                    onChange={(e) => setAttachTs(e.target.checked)}
                                    className="h-3.5 w-3.5 accent-[#E5A93D]"
                                />
                                Tijdstempel toevoegen <span className="text-[#E5A93D] font-mono">{fmtTs(currentTime)}</span>
                            </label>
                        </span>
                    )}
                </div>
                <div className="flex gap-2 items-start">
                    <Textarea
                        rows={2}
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                        placeholder={showVideo ? "Geef feedback op dit moment in de video…" : "Geef je feedback op deze foto…"}
                        className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] resize-none text-sm"
                        data-testid={`${testid}-comment-textarea`}
                    />
                    <Button
                        onClick={onSubmit}
                        disabled={submitting || !newText.trim()}
                        className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-10 px-4 active:scale-[0.98]"
                        data-testid={`${testid}-comment-submit-btn`}
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Plaats"}
                    </Button>
                </div>

                {/* Comments list */}
                {comments && comments.length > 0 && (
                    <div className="mt-4 space-y-2" data-testid={`${testid}-comments-list`}>
                        {comments.map((c) => (
                            <div key={c.id} className="bg-[#F4F4F5] border border-zinc-100 rounded-sm px-3 py-2 text-sm">
                                <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-1 flex-wrap">
                                    <User className="h-3 w-3" />
                                    <span className="text-zinc-950 font-medium">{c.author_name}</span>
                                    {c.timestamp_seconds != null && (
                                        <button
                                            type="button"
                                            onClick={() => seekTo(c.timestamp_seconds)}
                                            className="inline-flex items-center gap-1 text-[10px] font-mono text-[#E5A93D] hover:bg-[#E5A93D]/10 px-1.5 rounded-sm"
                                            data-testid="share-comment-jump"
                                        >
                                            <Clock className="h-2.5 w-2.5" /> {fmtTs(c.timestamp_seconds)}
                                        </button>
                                    )}
                                    <span className="text-zinc-500 ml-auto">{new Date(c.created_at).toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                                </div>
                                <div className="text-zinc-800 whitespace-pre-wrap">{c.text}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
